import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {TabSwitcherComponent} from '../../../../components/tab-switcher/tab-switcher.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Backend, PERMISSION} from '@generated-backend-types';
import {Subject, takeUntil} from 'rxjs';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {ActivatedRoute} from '@angular/router';
import {UserSessionService} from '../../../../services/user-session.service';
import {EmailService} from '../../../../services/api/administration/email.service';
import {ToastrService} from 'ngx-toastr';
import {FormErrorService} from '../../../../services/form-error.service';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {FormGroupComponent} from '../../../../components/form-inputs/form-group/form-group.component';
import {EditorComponent} from 'ngx-monaco-editor-v2';
import {NotEnoughPermissionsError} from '../../../../errors/NotEnoughPermissionsError';
import {HttpErrorResponse} from '@angular/common/http';
import Email = Backend.App.Resources.Dashboard.Administration.Email;
import PatientLanguageEnum = Backend.App.Enums.PatientLanguageEnum;
import EmailData = Backend.App.Data.Dashboard.Administration.EmailData;
import EmailPlaceholderEnum = Backend.App.Enums.Administration.EmailPlaceholderEnum;
import {EmailPreviewComponent} from '../../../../components/email-preview/email-preview.component';
import EmailPreviewData = Backend.App.Data.Dashboard.Administration.EmailPreviewData;

@Component({
  selector: 'app-authenticated-administration-emails-content',
  templateUrl: './content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TabSwitcherComponent,
    TranslatePipe,
    FormGroupComponent,
    ReactiveFormsModule,
    EditorComponent,
    EmailPreviewComponent,
  ],
})
export class ContentComponent extends AuthenticatedBaseComponent implements OnInit {
  public email?: Email;
  public language = PatientLanguageEnum.GERMAN;
  public form = new FormGroup({
    subject: new FormGroup({
      de: new FormControl('', [Validators.required]),
      en: new FormControl('', [Validators.required]),
      ru: new FormControl('', [Validators.required]),
    }),
    content: new FormGroup({
      de: new FormControl('', [Validators.required]),
      en: new FormControl('', [Validators.required]),
      ru: new FormControl('', [Validators.required]),
    }),
    pre_content: new FormGroup({
      de: new FormControl(''),
      en: new FormControl(''),
      ru: new FormControl(''),
    }),
    post_content: new FormGroup({
      de: new FormControl(''),
      en: new FormControl(''),
      ru: new FormControl(''),
    }),
  });
  public emailPreviewData$ = new Subject<EmailPreviewData>();

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly userSessionService: UserSessionService,
    private readonly emailService: EmailService,
    private readonly toastrService: ToastrService,
    private readonly formErrorService: FormErrorService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public get languageShort(): 'de' | 'en' | 'ru' {
    switch (this.language) {
      case PatientLanguageEnum.GERMAN:
        return 'de';

      case PatientLanguageEnum.ENGLISH:
        return 'en';

      case Backend.App.Enums.PatientLanguageEnum.RUSSIAN:
        return 'ru';
    }
  }

  public get formControls(): FormControl[] {
    return [
      this.form.controls.subject.controls[this.languageShort],
      this.form.controls.content.controls[this.languageShort],
      this.form.controls.pre_content.controls[this.languageShort],
      this.form.controls.post_content.controls[this.languageShort],
    ];
  }

  public ngOnInit() {
    this.activatedRoute.data.pipe(takeUntil(this.onDestroy$)).subscribe(data => {
      this.language = data['language'];

      const email: Email | undefined = data['email'];

      if (email) {
        this.email = email;
        this.form.patchValue(email);
        this.cdr.markForCheck();
      }

      this.titleService.setTitle('authenticated.administration.emails.content.' + this.language + '.title');
    });
  }

  public getFormControl(name: 'subject' | 'content' | 'pre_content' | 'post_content'): FormControl {
    return this.form.controls[name].controls[this.languageShort];
  }

  public get formIsValid(): boolean {
    return !this.formControls.map(control => control.valid).includes(false);
  }

  public save() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    const isValid = this.formIsValid;

    if (!isValid) {
      this.cdr.markForCheck();
      return
    }

    if (this.email) {
      if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_EMAIL_UPDATE_ALL])) {
        throw new NotEnoughPermissionsError();
      }

      this.emailService.update(this.email.id, this.getEmailData()).subscribe({
        next: res => {
          this.form.markAsPristine()
          this.form.markAsUntouched()
          this.toastrService.success(this.translate.instant('authenticated.administration.emails.content.' + this.language + '.success'));
          this.email = res.data;
          this.cdr.markForCheck();
        },
        error: err => {
          if (err instanceof NotEnoughPermissionsError) {
            this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
          } else if (err instanceof HttpErrorResponse) {
            this.form.markAllAsTouched();
            this.form.updateValueAndValidity({onlySelf: false, emitEvent: true});
            this.formErrorService.mapErrorToForm(this.form, err);
          } else {
            this.toastrService.error(this.translate.instant('authenticated.administration.emails.content.' + this.language + '.error'));
          }
          this.cdr.markForCheck();
        }
      });
    }
  }

  public showPreview() {
    this.emailPreviewData$.next(this.getEmailPreviewData());
  }

  private getEmailPreviewData(): EmailPreviewData {
    const formData = this.form.value as EmailData;

    return {
      language: this.language,
      content: formData.content,
      pre_content: formData.pre_content,
      post_content: formData.post_content,
      patient_id: null,
    };
  }

  private getEmailData(): EmailData {
    const data = this.email as EmailData;
    const formData = this.form.value as EmailData;

    data.subject = formData.subject;
    data.content = formData.content;
    data.pre_content = formData.pre_content;
    data.post_content = formData.post_content;

    return data;
  }

  protected readonly EmailPlaceholderEnum = EmailPlaceholderEnum;
  protected readonly Object = Object;
}
